import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import type { User, Progress } from "../types/api";
import { useQuery, useMutation } from "@tanstack/react-query";
import api from "../api/client";
import { Link } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import EmailCampaignModal from "../components/EmailCampaignModal";
import { toast } from "react-hot-toast";
import {
  UserIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  BuildingLibraryIcon,
  PencilIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentCheckIcon,
  MagnifyingGlassIcon,
  ShieldExclamationIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import TagBadge from "../components/TagBadge";

const STAGES = [
  { key: 'accountCreated', label: 'Account Created', icon: UserIcon },
  { key: 'emailConfirmed', label: 'Email Confirmed', icon: EnvelopeIcon },
  { key: 'documentsUploaded', label: 'Documents Uploaded', icon: DocumentTextIcon },
  { key: 'bankingProvided', label: 'Banking Provided', icon: BuildingLibraryIcon },
  { key: 'contractStarted', label: 'Contract Started', icon: PencilIcon },
  { key: 'contractCompleted', label: 'Contract Completed', icon: DocumentCheckIcon },
  { key: 'payoutScheduled', label: 'Payout Scheduled', icon: ClockIcon },
  { key: 'payoutExecuted', label: 'Payout Executed', icon: CurrencyDollarIcon },
];

function getLastChangedDate(progress?: Progress): string | null {
  if (!progress?.completedAt) return null;
  const dates = Object.values(progress.completedAt)
    .filter((d): d is string | Date => !!d)
    .map(d => new Date(d).getTime());
  
  if (dates.length === 0) return null;
  return new Date(Math.max(...dates)).toLocaleDateString();
}

function StageProgress({ progress }: { progress?: Progress }) {
  const completedAt = progress?.completedAt || {};

  return (
    <div className="flex items-center space-x-1">
      {STAGES.map((stage) => {
        const isCompleted = !!completedAt[stage.key as keyof typeof completedAt];
        const date = completedAt[stage.key as keyof typeof completedAt];
        
        return (
          <div 
            key={stage.key} 
            className="relative group cursor-help"
            title={`${stage.label}${isCompleted && date ? `: ${new Date(date).toLocaleDateString()}` : ': Pending'}`}
          >
            <stage.icon 
              className={`h-5 w-5 ${
                isCompleted 
                  ? 'text-green-500 dark:text-green-400' 
                  : 'text-gray-300 dark:text-gray-600'
              }`} 
            />
          </div>
        );
      })}
    </div>
  );
}

// ... imports ...

// ... STAGES ...

// ... helper functions ...

const columnHelper = createColumnHelper<User>();

export default function Claimants() {
  'use no memo'; // Opt out of React Compiler - useReactTable returns unstable references
  const [page, setPage] = useState(1);
  const [step, setStep] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedTag, setSelectedTag] = useState<string>("");
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
        setDebouncedSearch(search);
        setPage(1); // Reset to page 1 on new search
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [step, startDate, endDate, selectedTag]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["claimants", page, step, startDate, endDate, debouncedSearch, selectedTag],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20",
        ...(step && { step }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(selectedTag && { tag: selectedTag }),
      });
      const res = await api.get(`/admin/claimants?${params.toString()}`);
      return res.data;
    },
  });

  // Fetch available tags for display
  const { data: tagsData } = useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await api.get("/admin/tags");
      return res.data.data as { _id: string; name: string; color: string }[];
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        cell: (info) => (
          <div className="font-medium text-gray-900 dark:text-white">
            {info.getValue() || "N/A"}
          </div>
        ),
      }),
      columnHelper.accessor("email", {
        header: "Email",
        cell: (info) => (
          <div className="text-gray-500 dark:text-gray-400">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor("progress", {
        header: "Stage",
        cell: (info) => <StageProgress progress={info.getValue()} />,
      }),
      columnHelper.display({
        id: "issues",
        header: "Issues",
        cell: (props) => {
          const user = props.row.original;
          const riskLevel = user.riskLevel;
          const docusignError = user.docusign?.deliveryStatus === 'failed' || user.docusign?.deliveryStatus === 'autoresponded' || user.docusign?.deliveryStatus === 'authfailed';
          const postmarkError = user.postmark?.deliveryStatus === 'bounced' || user.postmark?.deliveryStatus === 'spam_complaint';

          const hasIssues = (riskLevel && (riskLevel === 'high' || riskLevel === 'critical' || riskLevel === 'medium')) || docusignError || postmarkError;

          if (!hasIssues) {
            return <CheckCircleIcon className="h-5 w-5 text-green-500" title="No issues" />;
          }

          return (
            <div className="flex items-center gap-1">
              {/* Fraud risk indicator - Shield icon */}
              {riskLevel === 'critical' && (
                <ShieldExclamationIcon className="h-5 w-5 text-red-600 cursor-help" title="Critical fraud risk" />
              )}
              {riskLevel === 'high' && (
                <ShieldExclamationIcon className="h-5 w-5 text-red-500 cursor-help" title="High fraud risk" />
              )}
              {riskLevel === 'medium' && (
                <ShieldExclamationIcon className="h-5 w-5 text-yellow-500 cursor-help" title="Medium fraud risk" />
              )}
              {/* DocuSign delivery error indicator - Document icon */}
              {docusignError && (
                <DocumentCheckIcon className="h-5 w-5 text-orange-500 cursor-help" title={`DocuSign: ${user.docusign?.deliveryError || 'Delivery issue'}`} />
              )}
              {/* Postmark email delivery error indicator - Envelope icon */}
              {postmarkError && (
                <EnvelopeIcon className="h-5 w-5 text-orange-500 cursor-help" title={`Email: ${user.postmark?.deliveryError || user.postmark?.deliveryStatus}`} />
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor("tags", {
        header: "Tags",
        cell: (info) => {
          const userTags = info.getValue() || [];
          if (userTags.length === 0) return <span className="text-gray-400 text-xs">-</span>;
          return (
            <div className="flex flex-wrap gap-1 max-w-[150px]">
              {userTags.slice(0, 3).map((tagName: string) => {
                const tagInfo = tagsData?.find(t => t.name === tagName);
                return tagInfo ? (
                  <TagBadge key={tagName} name={tagInfo.name} color={tagInfo.color} />
                ) : (
                  <span key={tagName} className="px-1.5 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 rounded-full">
                    {tagName}
                  </span>
                );
              })}
              {userTags.length > 3 && (
                <span className="text-xs text-gray-500">+{userTags.length - 3}</span>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor("eligibleGmv", {
        header: "GMV",
        cell: (info) => {
          const val = info.getValue();
          return (
            <div className="text-gray-500 dark:text-gray-400">
              {typeof val === 'number'
                ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val)
                : '-'}
            </div>
          );
        },
      }),
      columnHelper.accessor("purchasePrice", {
        header: "Price",
        cell: (info) => {
          const val = info.getValue();
          return (
            <div className="text-gray-500 dark:text-gray-400">
              {typeof val === 'number'
                ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val)
                : '-'}
            </div>
          );
        },
      }),
      columnHelper.accessor("progress", {
        id: "lastChanged",
        header: "Last Changed",
        cell: (info) => {
           const date = getLastChangedDate(info.getValue());
           return <div className="text-gray-500 dark:text-gray-400">{date || "N/A"}</div>
        },
      }),
      columnHelper.accessor("createdAt", {
        header: "Created At",
        cell: (info) => (
          <div className="text-gray-500 dark:text-gray-400">
            {new Date(info.getValue()).toLocaleDateString()}
          </div>
        ),
      }),
      columnHelper.display({
        id: "actions",
        cell: (props) => (
          <div className="text-right font-medium">
            <Link
              to={`/claimants/${props.row.original._id}`}
              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
            >
              View<span className="sr-only">, {props.row.original.name}</span>
            </Link>
          </div>
        ),
      }),
    ],
    [tagsData]
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const sendCampaignMutation = useMutation({
    mutationFn: async (data: { filter: Record<string, unknown>; subject: string; content: string }) => {
      return api.post("/admin/campaign", data);
    },
    onSuccess: (data) => {
      toast.success(data.data.message);
      setIsCampaignModalOpen(false);
    },
    onError: () => {
      toast.error("Failed to send campaign");
    },
  });

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        ...(step && { step }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      });
      const response = await api.get(`/admin/claimants/export?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "claimants.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export failed", error);
    }
  };

  const handleSendCampaign = (subject: string, content: string) => {
    const filter: Record<string, string | number> = {};
    if (step) filter.step = parseInt(step);
    
    sendCampaignMutation.mutate({
      filter,
      subject,
      content
    });
  };
    
  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading claimants</div>;

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Claimants</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            A list of all claimants including their name, email, and status.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex flex-wrap gap-2 items-end">
            <div className="relative rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                    type="text"
                    name="search"
                    id="search"
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 pl-10 text-gray-900 dark:text-white sm:text-sm focus:border-indigo-500 focus:ring-indigo-500 placeholder:text-gray-400"
                    placeholder="Search name or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
          <div className="w-56"> 
            <label htmlFor="step" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Stage</label>
            <Listbox value={step} onChange={setStep}>
              <div className="relative mt-1">
                <ListboxButton className="relative w-full cursor-default rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white sm:leading-6">
                  <span className="flex items-center truncate">
                    {(() => {
                        const selectedStage = STAGES.find(s => s.key === step);
                        if (selectedStage) {
                            return (
                                <>
                                    <selectedStage.icon className="mr-2 h-5 w-5 text-gray-500" aria-hidden="true" />
                                    {selectedStage.label}
                                </>
                            );
                        }
                        return "All Stages";
                    })()}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <ListboxOption
                    value=""
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-3 pr-9 ${
                        active ? "bg-indigo-600 text-white" : "text-gray-900 dark:text-white"
                      }`
                    }
                  >
                    {({ selected, active }) => (
                      <>
                        <span className={`block truncate ${selected ? "font-semibold" : "font-normal"}`}>
                          All Stages
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                              active ? "text-white" : "text-indigo-600"
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ListboxOption>
                  {STAGES.map((stage) => (
                    <ListboxOption
                      key={stage.key}
                      value={stage.key}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-3 pr-9 ${
                          active ? "bg-indigo-600 text-white" : "text-gray-900 dark:text-white"
                        }`
                      }
                    >
                      {({ selected, active }) => (
                        <>
                            <div className="flex items-center">
                                <stage.icon className={`mr-2 h-5 w-5 ${active ? "text-white" : "text-gray-500"}`} aria-hidden="true" />
                                <span className={`block truncate ${selected ? "font-semibold" : "font-normal"}`}>
                                    {stage.label}
                                </span>
                            </div>
                          {selected ? (
                            <span
                              className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                active ? "text-white" : "text-indigo-600"
                              }`}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
          </div>

          {/* Tag Filter */}
          <div className="w-44">
            <label htmlFor="tag" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Tag</label>
            <Listbox value={selectedTag} onChange={setSelectedTag}>
              <div className="relative mt-1">
                <ListboxButton className="relative w-full cursor-default rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white sm:leading-6">
                  <span className="flex items-center truncate">
                    {(() => {
                      const tag = tagsData?.find(t => t.name === selectedTag);
                      if (tag) {
                        return (
                          <>
                            <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: tag.color }} />
                            {tag.name}
                          </>
                        );
                      }
                      return "All Tags";
                    })()}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <ListboxOption
                    value=""
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-3 pr-9 ${
                        active ? "bg-indigo-600 text-white" : "text-gray-900 dark:text-white"
                      }`
                    }
                  >
                    {({ selected, active }) => (
                      <>
                        <span className={`block truncate ${selected ? "font-semibold" : "font-normal"}`}>
                          All Tags
                        </span>
                        {selected && (
                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? "text-white" : "text-indigo-600"}`}>
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </ListboxOption>
                  {tagsData?.map((tag) => (
                    <ListboxOption
                      key={tag._id}
                      value={tag.name}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-3 pr-9 ${
                          active ? "bg-indigo-600 text-white" : "text-gray-900 dark:text-white"
                        }`
                      }
                    >
                      {({ selected, active }) => (
                        <>
                          <div className="flex items-center">
                            <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: tag.color }} />
                            <span className={`block truncate ${selected ? "font-semibold" : "font-normal"}`}>
                              {tag.name}
                            </span>
                          </div>
                          {selected && (
                            <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? "text-white" : "text-indigo-600"}`}>
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
          </div>

          <div>
            <label htmlFor="startDate" className="block text-xs font-medium text-gray-700 dark:text-gray-300">From</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-2 px-3 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-xs font-medium text-gray-700 dark:text-gray-300">To</label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-2 px-3 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <button
            onClick={handleExport}
            type="button"
            className="mb-0.5 inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Export CSV
          </button>
        </div>
      </div>
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black dark:ring-white ring-opacity-5 dark:ring-opacity-10 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white first:pl-4 first:sm:pl-6 last:pr-4 last:sm:pr-6"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400 first:pl-4 first:sm:pl-6 last:pr-4 last:sm:pr-6"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
       
      {/* Pagination */}
      <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 mt-4">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!data || data.data.length < 20}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Showing <span className="font-medium">{data?.data?.length || 0}</span> of{" "}
              <span className="font-medium">{new Intl.NumberFormat('de-DE').format(data?.pagination?.total || 0)}</span> claimants{" "}
              (Page <span className="font-medium">{page}</span> of{" "}
              <span className="font-medium">{Math.ceil((data?.pagination?.total || 0) / 20)}</span>)
            </p>
          </div>
          <div>
            <nav
              className="isolate inline-flex -space-x-px rounded-md shadow-sm"
              aria-label="Pagination"
            >
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 focus:z-20 disabled:opacity-50"
              >
                <span className="sr-only">Previous</span>
                Previous
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!data || data.data.length < 20}
                className="relative inline-flex items-center rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 focus:z-20 disabled:opacity-50"
              >
                <span className="sr-only">Next</span>
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>

       <EmailCampaignModal
        isOpen={isCampaignModalOpen}
        onClose={() => setIsCampaignModalOpen(false)}
        onSend={handleSendCampaign}
        recipientCount={data?.pagination?.total || 0}
      />
    </div>
  );
}
